// /admin/v2/notes — Notes loader (BRIEFS C4.4, SPEC §7): choose one chapter's notes.json →
// "Check the file" (dry-run, nothing saved) → a plain-words report of what will change
// (including how many of Harsh's highlights are touched) → "Save these changes" (apply).
import React, { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { v2NotesDryRun, v2NotesApply, errText } from '../../../api/v2admin';
import V2Tabs, { Msg } from './V2Tabs';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function List({ items, render, max = 12 }) {
  if (!items || !items.length) return null;
  return (
    <ul style={{ margin: '6px 0 0 18px', fontSize: 13, color: 'var(--text-muted)' }}>
      {items.slice(0, max).map((x, i) => <li key={i}>{render(x)}</li>)}
      {items.length > max && <li>… and {items.length - max} more</li>}
    </ul>
  );
}

function Line({ n, text, children, tone }) {
  if (!n) return null;
  return (
    <li style={{ marginBottom: 10, color: tone || 'var(--text)' }}>
      <span style={{ fontWeight: 600 }}>{text}</span>
      {children}
    </li>
  );
}

export function Report({ r, section = false }) {
  const reworded = r.points_reworded || [];
  const hlTouched = reworded.reduce((a, x) => a + (x.highlights || 0), 0)
    + (r.points_removed || []).reduce((a, x) => a + (x.highlights || 0), 0);
  const hlLost = reworded.reduce((a, x) => a + (x.highlights_lost || 0), 0)
    + (r.points_removed || []).reduce((a, x) => a + (x.highlights || 0), 0);
  const c = r.counts || {};
  return (
    <div data-testid="v2-notes-report">
      {r.errors && r.errors.length > 0 && (
        <div style={{ marginBottom: 14, color: '#B91C1C' }}>
          <div style={{ fontWeight: 700 }}>
            The file has {plural(r.errors.length, 'problem', 'problems')}. Nothing can be saved until {r.errors.length === 1 ? 'it is' : 'they are'} fixed:
          </div>
          <List items={r.errors} max={30} render={(e) => <span style={{ color: '#B91C1C' }}>{e}</span>} />
        </div>
      )}
      {r.chapter_code && !section && (
        <p style={{ marginBottom: 10 }}>
          This file is for <strong>{r.chapter_code}</strong> (chapter id {r.chapter_id}):
          {' '}{plural(c.units || 0, 'unit', 'units')}, {plural(c.points || 0, 'point', 'points')} in the file.
        </p>
      )}
      {!r.errors?.length && !r.has_changes && (
        <p style={{ fontWeight: 600, color: '#047857' }}>The file is the same as the notes already saved. Nothing to change.</p>
      )}
      {r.has_changes && (
        <ul style={{ margin: '0 0 0 18px', padding: 0 }}>
          <Line n={r.units_added?.length} text={`New units: ${r.units_added?.length}`}>
            <List items={r.units_added} render={(x) => x} />
          </Line>
          <Line n={r.units_changed?.length} text={`Units with a changed title, number, page or place: ${r.units_changed?.length}`}>
            <List items={r.units_changed} render={(x) => `${x.code} (${x.fields.join(', ')})`} />
          </Line>
          <Line n={r.units_removed?.length} tone="#B91C1C" text={`Units that will be hidden: ${r.units_removed?.length}`}>
            <List items={r.units_removed} render={(x) => x} />
          </Line>
          <Line n={r.points_added?.length} text={`New points: ${r.points_added?.length}`}>
            <List items={r.points_added} max={8} render={(x) => x} />
          </Line>
          <Line n={reworded.length} text={`Points with new wording: ${reworded.length} (the old wording is kept in the history)`}>
            <List items={reworded} max={8} render={(x) => (
              <span><strong>{x.code}</strong>: “{x.old}” → “{x.new}”{x.highlights ? ` — Harsh has ${plural(x.highlights, 'highlight', 'highlights')} here` : ''}</span>
            )} />
          </Line>
          <Line n={r.points_removed?.length} tone="#B91C1C" text={`Points that will be hidden: ${r.points_removed?.length}`}>
            <List items={r.points_removed} render={(x) => `${x.code}: “${x.text}”`} />
          </Line>
          <Line n={r.points_restored?.length} text={`Hidden points that come back: ${r.points_restored?.length}`} />
          <Line n={r.points_moved?.length} text={`Points in a new place: ${r.points_moved?.length}`} />
          <Line n={r.points_updated?.length} text={`Points with a changed source or page: ${r.points_updated?.length}`} />
          <Line n={r.images_changed?.length} text={`Figures added, changed or taken out: ${r.images_changed?.length}`}>
            <List items={r.images_changed} render={(x) => `${x.unit}: ${x.url} (${x.change})`} />
          </Line>
        </ul>
      )}
      {r.has_changes && (
        <p data-testid="v2-notes-highlights" style={{ marginTop: 6, fontWeight: 600, color: hlLost ? '#B45309' : 'var(--text-muted)' }}>
          {hlTouched === 0
            ? 'No highlight of a student is touched.'
            : `${plural(hlTouched, 'student highlight is', 'student highlights are')} on changed points; ${plural(hlLost, 'needs', 'need')} checking because the marked words are gone.`}
        </p>
      )}
      {!section && r.points_assigned && r.points_assigned.some((a) => !a.existing) && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontWeight: 600 }}>Points written as "new" get these codes — put them into the file:</div>
          <List items={r.points_assigned.filter((a) => !a.existing)} render={(a) => `${a.unit} → ${a.code}`} />
        </div>
      )}
      {r.warnings && r.warnings.length > 0 && (
        <div style={{ marginTop: 10, color: '#B45309' }}>
          <div style={{ fontWeight: 600 }}>Please look at:</div>
          <List items={r.warnings} render={(w) => w} />
        </div>
      )}
    </div>
  );
}

export default function AdminV2Notes() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);           // {name, json}
  const [allowRemove, setAllowRemove] = useState(false);
  const [report, setReport] = useState(null);       // last dry-run report
  const [saved, setSaved] = useState(null);         // last apply report
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const reset = () => { setReport(null); setSaved(null); setMsg(null); };

  const pick = (f) => {
    reset(); setFile(null);
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => {
      try {
        const json = JSON.parse(String(rd.result || ''));
        setFile({ name: f.name, json });
      } catch {
        setMsg({ kind: 'err', text: `${f.name} is not a JSON file that can be read. Please check the file.` });
      }
    };
    rd.onerror = () => setMsg({ kind: 'err', text: 'Could not read the file.' });
    rd.readAsText(f, 'utf-8');
  };

  const payload = () => ({ ...file.json, allow_remove: allowRemove });

  const check = async () => {
    setBusy(true); setSaved(null); setMsg(null);
    try {
      setReport(await v2NotesDryRun(payload()));
    } catch (e) {
      setMsg({ kind: 'err', text: errText(e, 'Could not check the file.') });
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!window.confirm('Save these changes into the notes now?')) return;
    setBusy(true); setMsg(null);
    try {
      const r = await v2NotesApply(payload());
      if (r.applied) {
        setSaved(r); setReport(null);
        setMsg({ kind: 'ok', text: `Saved. The notes of ${r.chapter_code} are updated. Harsh sees the new notes the next time he opens the chapter.` });
        qc.invalidateQueries({ queryKey: ['v2-admin-chapters'] });
      } else {
        setReport(r);
        setMsg({ kind: 'err', text: r.errors?.length ? 'Nothing was saved: the file has problems (see below).' : 'Nothing was saved: there was nothing to change.' });
      }
    } catch (e) {
      setMsg({ kind: 'err', text: errText(e, 'Could not save the file.') });
    } finally { setBusy(false); }
  };

  const canSave = report && !report.errors?.length && report.has_changes && !busy;

  return (
    <div>
      <V2Tabs title="Notes loader (whole file)"
        intro="Advanced. Load one chapter's whole notes.json — only for a chapter that has never been released, and its NCERT sections must stay exactly as NCERT prints them. For everyday changes use Notes (by chapter): one section at a time. First the file is checked and you see what will change — nothing is saved yet. Then press Save." />
      <Msg msg={msg} />
      <div className="card" style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input ref={fileRef} type="file" accept=".json,application/json" data-testid="v2-notes-file"
            onChange={(e) => pick(e.target.files[0])} style={{ fontSize: 14 }} />
          {file && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{file.name}</span>}
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
          <input type="checkbox" checked={allowRemove} onChange={(e) => { setAllowRemove(e.target.checked); setReport(null); }} />
          Also hide points and units that are no longer in the file
        </label>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost" disabled={!file || busy} onClick={check} data-testid="v2-notes-check">
            1. Check the file
          </button>
          <button className="btn btn-primary" disabled={!canSave} onClick={save} data-testid="v2-notes-save">
            2. Save these changes
          </button>
        </div>
      </div>
      {report && <div className="card"><Report r={report} /></div>}
      {saved && (
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 8 }}>What was saved</div>
          <Report r={saved} />
        </div>
      )}
    </div>
  );
}
